import React, { useState, Component } from 'react';
import { CopyIcon, CheckIcon } from 'lucide-react';
import { DocsLayout } from '../components/DocsLayout';
const tocItems = [{
  id: 'sdk',
  label: 'Using the OpenFPGA SDK (Beta)'
}, {
  id: 'api',
  label: 'Using the OpenFPGA API directly'
}, {
  id: 'openai',
  label: 'Using the OpenAI-Compatible SDK'
}, {
  id: 'third-party',
  label: 'Using Third-Party SDKs'
}];
// ─── Code Block Component ────────────────────────────────────────
function CodeBlock({
  code,
  language
}: {
  code: string;
  language: string;
}) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <div className="relative group rounded-lg border border-gate-border bg-[#0d1117] overflow-hidden" data-id="element-426">
      <div className="flex items-center justify-between px-4 py-2 border-b border-gate-border/50 bg-gate-surface/30" data-id="element-427">
        <span className="text-xs font-mono text-gate-text-muted" data-id="element-428">
          {language}
        </span>
        <button onClick={handleCopy} className="flex items-center gap-1.5 text-xs text-gate-text-muted hover:text-gate-teal transition-colors" aria-label="Copy code" data-id="element-429">
          {copied ? <>
              <CheckIcon className="w-3.5 h-3.5" data-id="element-430" />
              <span data-id="element-431">Copied</span>
            </> : <>
              <CopyIcon className="w-3.5 h-3.5" data-id="element-432" />
              <span data-id="element-433">Copy</span>
            </>}
        </button>
      </div>
      <pre className="p-4 overflow-x-auto text-sm leading-relaxed" data-id="element-434">
        <code className="font-mono text-gate-text-primary" data-id="element-435">{code}</code>
      </pre>
    </div>;
}
// ─── Tab Switcher for Package Managers ───────────────────────────
function PackageInstall() {
  const [activeTab, setActiveTab] = useState<'npm' | 'yarn' | 'pnpm'>('npm');
  const commands = {
    npm: 'npm install @openfpga/sdk',
    yarn: 'yarn add @openfpga/sdk',
    pnpm: 'pnpm add @openfpga/sdk'
  };
  return <div className="rounded-lg border border-gate-border bg-[#0d1117] overflow-hidden" data-id="element-436">
      <div className="flex items-center border-b border-gate-border/50 bg-gate-surface/30" data-id="element-437">
        {(['npm', 'yarn', 'pnpm'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`relative px-4 py-2 text-sm font-mono transition-colors ${activeTab === tab ? 'text-gate-teal' : 'text-gate-text-muted hover:text-gate-text-secondary'}`} data-id="element-438">
            {tab}
            {activeTab === tab && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gate-teal" data-id="element-439" />}
          </button>)}
      </div>
      <div className="p-4" data-id="element-440">
        <code className="font-mono text-sm text-gate-text-primary" data-id="element-441">
          <span className="text-gate-text-muted select-none" data-id="element-442">$ </span>
          {commands[activeTab]}
        </code>
      </div>
    </div>;
}
// ─── Main Docs Page ─────────────────────────────────────────────
export function DocsPage() {
  const sdkCode = `import OpenFPGA from '@openfpga/sdk'

const client = new OpenFPGA({
  apiKey: process.env.OPENFPGA_API_KEY,
})

async function main() {
  const result = await client.inference.create({
    model: 'xilinx/alveo-u250',
    input: {
      data: [1.0, 2.0, 3.0, 4.0],
      dtype: 'float32',
    },
  })

  console.log(result.output)
  console.log(result.latency_ms)
}

main()`;
  const curlCode = `curl https://api.openfpga.ai/v1/inference \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer $OPENFPGA_API_KEY" \\
  -d '{
    "model": "xilinx/alveo-u250",
    "input": {
      "data": [1.0, 2.0, 3.0, 4.0],
      "dtype": "float32"
    }
  }'`;
  const pythonCode = `from openai import OpenAI

client = OpenAI(
    base_url="https://api.openfpga.ai/v1",
    api_key="your-openfpga-api-key",
)

result = client.chat.completions.create(
    model="xilinx/alveo-u250",
    messages=[{
        "role": "user",
        "content": "Run inference on this data"
    }],
)

print(result.choices[0].message.content)`;
  return <DocsLayout activePage="Quickstart" breadcrumb="Quickstart" tocItems={tocItems} data-id="element-443">
      {/* Title */}
      <h1 className="text-4xl font-bold text-white mb-2 tracking-tight" data-id="element-444">
        Quickstart
      </h1>
      <p className="text-lg text-gate-text-secondary mb-10" data-id="element-445">
        Get started with OpenFPGA
      </p>

      {/* Intro */}
      <p className="text-gate-text-secondary leading-relaxed mb-8" data-id="element-446">
        OpenFPGA provides a unified API that gives you access to FPGA
        accelerators across multiple vendors through a single endpoint, while
        automatically handling fallbacks and selecting the most cost-effective
        options. Get started with just a few lines of code using your preferred
        SDK or framework.
      </p>

      {/* Info callout */}
      <div className="rounded-lg border border-gate-teal/20 bg-gate-teal/5 p-4 mb-10" data-id="element-447">
        <p className="text-sm text-gate-text-secondary" data-id="element-448">
          In the examples below, the OpenFPGA-specific headers are optional. For
          detailed information about app attribution, see our{' '}
          <a href="#" className="text-gate-teal hover:underline" data-id="element-449">
            App Attribution guide
          </a>
          .
        </p>
      </div>

      {/* Section 1: SDK */}
      <section className="mb-14" data-id="element-450">
        <h2 id="sdk" className="text-2xl font-bold text-white mb-4 scroll-mt-24" data-id="element-451">
          Using the OpenFPGA SDK (Beta)
        </h2>
        <p className="text-gate-text-secondary mb-4" data-id="element-452">First, install the SDK:</p>
        <div className="mb-6" data-id="element-453">
          <PackageInstall data-id="element-454" />
        </div>
        <p className="text-gate-text-secondary mb-4" data-id="element-455">
          Then, create an inference request:
        </p>
        <CodeBlock code={sdkCode} language="typescript" data-id="element-456" />
      </section>

      {/* Section 2: API */}
      <section className="mb-14" data-id="element-457">
        <h2 id="api" className="text-2xl font-bold text-white mb-4 scroll-mt-24" data-id="element-458">
          Using the OpenFPGA API directly
        </h2>
        <p className="text-gate-text-secondary mb-4" data-id="element-459">
          You can also call the API directly using{' '}
          <code className="font-mono text-sm text-gate-teal bg-gate-teal/10 px-1.5 py-0.5 rounded" data-id="element-460">
            curl
          </code>{' '}
          or any HTTP client:
        </p>
        <CodeBlock code={curlCode} language="bash" data-id="element-461" />
      </section>

      {/* Section 3: OpenAI SDK */}
      <section className="mb-14" data-id="element-462">
        <h2 id="openai" className="text-2xl font-bold text-white mb-4 scroll-mt-24" data-id="element-463">
          Using the OpenAI-Compatible SDK
        </h2>
        <p className="text-gate-text-secondary mb-4" data-id="element-464">
          OpenFPGA's API is fully compatible with the OpenAI SDK. Simply change
          the{' '}
          <code className="font-mono text-sm text-gate-teal bg-gate-teal/10 px-1.5 py-0.5 rounded" data-id="element-465">
            base_url
          </code>{' '}
          to point to OpenFPGA:
        </p>
        <CodeBlock code={pythonCode} language="python" data-id="element-466" />
      </section>

      {/* Section 4: Third-Party */}
      <section className="mb-14" data-id="element-467">
        <h2 id="third-party" className="text-2xl font-bold text-white mb-4 scroll-mt-24" data-id="element-468">
          Using Third-Party SDKs
        </h2>
        <p className="text-gate-text-secondary leading-relaxed" data-id="element-469">
          Any SDK or library that supports the OpenAI API format will work with
          OpenFPGA out of the box. Simply set the base URL to{' '}
          <code className="font-mono text-sm text-gate-teal bg-gate-teal/10 px-1.5 py-0.5 rounded" data-id="element-470">
            https://api.openfpga.ai/v1
          </code>{' '}
          and use your OpenFPGA API key. This includes libraries in Go, Rust,
          Java, Ruby, and any other language with an OpenAI-compatible client.
        </p>
      </section>
    </DocsLayout>;
}